import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

// Variant vocabulary matches Button (primary/secondary/ghost). The default is
// secondary — icon buttons are quieter by default because they usually sit in
// clusters (toolbars, rows) where a filled default would shout.
export type IconButtonVariant = 'primary' | 'secondary' | 'ghost';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible label for screen readers. Required since the icon alone is not descriptive. */
  label: string;
  icon: ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

// Hover gated behind `can-hover` so touch devices don't get stuck-hover.
const variantClasses: Record<IconButtonVariant, string> = {
  primary: 'bg-t1 text-canvas can-hover:hover:bg-t1-hover',
  secondary:
    'bg-card text-t2 border border-line-2 can-hover:hover:border-line-3 can-hover:hover:text-t1',
  ghost: 'bg-transparent text-t2 can-hover:hover:bg-rail can-hover:hover:text-t1',
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: 'h-7 w-7 rounded-md pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
  md: 'h-[30px] w-[30px] rounded-md pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
  lg: 'h-9 w-9 rounded-lg pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'secondary', size = 'md', className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      data-variant={variant}
      data-size={size}
      className={cn(
        'pressable inline-flex shrink-0 items-center justify-center focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
    </button>
  );
});
